import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button,
} from '../ui'
import FeaturedLayout from '../layouts/FeaturedLayout'
import { useAuth } from '../../hooks/useAuth'
import { useOrganization } from '../../contexts/OrganizationContext'
import { createPlanChangeRequest } from '../../lib/supabase'
import { FEATURE_REQUIRED_PLAN } from '../../lib/entitlements'

const planCodeFromName = (name) => (name === 'Pro' ? 'pro' : 'club')

const useUpsellRequest = () => {
  const { staff } = useAuth()
  const { org } = useOrganization()
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const request = async (requiredPlanName) => {
    if (!org?.id || !staff?.id) return
    setSending(true)
    const { error } = await createPlanChangeRequest({
      orgId: org.id, toPlanCode: planCodeFromName(requiredPlanName), staffId: staff.id,
      note: 'Solicitud desde upsell',
    })
    setSending(false)
    if (!error) setSent(true)
  }
  return { request, sending, sent }
}

const Body = ({ requiredPlan, message, navigate, request, sending, sent }) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">
      {message || `Esta función está disponible en el plan ${requiredPlan}.`}
    </p>
    {sent ? (
      <p className="text-sm font-medium text-green-600">
        Solicitud enviada. Un administrador la revisará pronto.
      </p>
    ) : (
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => navigate('/billing/plans')}>Ver planes</Button>
        <Button variant="outline" disabled={sending} onClick={() => request(requiredPlan)}>
          {sending ? 'Enviando...' : 'Solicitar mejora de plan'}
        </Button>
      </div>
    )}
  </div>
)

export const UpsellModal = ({ open, onOpenChange, feature, title, message }) => {
  const navigate = useNavigate()
  const { request, sending, sent } = useUpsellRequest()
  const requiredPlan = FEATURE_REQUIRED_PLAN[feature] || 'Club'
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" /> {title || 'Mejora tu plan'}
          </DialogTitle>
        </DialogHeader>
        <Body requiredPlan={requiredPlan} message={message} navigate={navigate} request={request} sending={sending} sent={sent} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const UpsellScreen = ({ feature }) => {
  const navigate = useNavigate()
  const { request, sending, sent } = useUpsellRequest()
  const requiredPlan = FEATURE_REQUIRED_PLAN[feature] || 'Club'
  return (
    <FeaturedLayout title="Función no disponible en tu plan">
      <div className="mx-auto max-w-md rounded-xl border p-8 text-center">
        <Lock className="mx-auto h-10 w-10 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold mb-2">Disponible en el plan {requiredPlan}</h2>
        <Body requiredPlan={requiredPlan} message={null} navigate={navigate} request={request} sending={sending} sent={sent} />
      </div>
    </FeaturedLayout>
  )
}
